import React, { useState } from 'react'
import { Form, Input, Modal, Select } from 'antd'
import { SubmitHandler, useForm } from 'react-hook-form'
import { yupResolver } from '@hookform/resolvers/yup'
import * as yup from 'yup'
import { useTranslation } from 'react-i18next'
import { CloseOutlined } from '@ant-design/icons'

import { Truck } from '@types'
import { useEquipment, useUserResponseManager } from '@hooks'
import { useUser } from '@contexts'

import { StyledForm, StyledRow, StyledCheckbox } from '../../../../../components/StyledComponents'

type Props = {
  open: boolean
  onClose: () => void
  onAdd: (data: Truck) => void
}

const AddTruckModal: React.FC<Props> = ({ open, onClose, onAdd }) => {
  const { t } = useTranslation()
  const { equipments } = useEquipment()
  const { state: user } = useUser()
  const { getUser } = useUserResponseManager(user?.id)
  const trucks = getUser?.user?.userDetail?.carrier?.trucks

  const validationSchema = yup.object().shape({
    licensePlate: yup
      .string()
      .required('Campo obligatorio')
      .min(5, 'La matrícula debe tener más de 5 caracteres')
      .max(10, 'La matrícula no debe tener más de 10 caracteres')
      .matches(
        /^[A-Z0-9\s]*$/,
        'La matrícula debe contener caracteres alfanuméricos y ser todas las letras mayusculas',
      )
      .test('no-only-spaces', 'La matrícula no puede consistir solo en espacios', value => {
        if (value?.trim() === '') {
          throw new yup.ValidationError(
            'La matrícula no puede consistir solo en espacios',
            value,
            'licensePlate',
          )
        }
        return true
      })
      .test(
        'unique-state-id',
        'Ya existe un vehículo con la misma matrícula',
        value => !trucks?.some(truck => truck?.licensePlate === value),
      ),
    description: yup
      .string()
      .required('Campo obligatorio')
      .max(20, 'La descripción no debe tener más de 20 caracteres')
      .matches(/^[a-zA-ZÀ-ÿ0-9\s]*$/, 'La descripción debe contener caracteres alfanuméricos')
      .test('no-only-spaces', 'La descripción no puede consistir solo en espacios', value => {
        if (value?.trim() === '') {
          throw new yup.ValidationError(
            'La descripción no puede consistir solo en espacios',
            value,
            'description',
          )
        }
        return true
      }),
    equipmentType: yup.object().required('Campo obligatorio'),
  })

  const { reset, handleSubmit, register, setValue, formState } = useForm<{
    licensePlate: string
    description: string
    straightTruck: boolean
    equipmentType: { id: unknown }
  }>({
    resolver: yupResolver(validationSchema),
  })

  const [includeTrailer, setIncludeTrailer] = useState(false)

  const handleIncludeTrailerChange = (checked: boolean) => {
    setIncludeTrailer(checked)
    if (checked) {
      setValue('equipmentType', { id: null })
      setValue('straightTruck', false)
    }
  }

  const onSubmit = (data: Truck) => {
    onAdd(data)
    reset()
    setIncludeTrailer(false)
  }

  return (
    <Modal
      open={open}
      keyboard
      maskClosable
      onCancel={() => {
        reset()
        onClose()
      }}
      title={t('tpl_router.tpl_dashboard.add_truck_modal.title')}
      closeIcon={<CloseOutlined />}
      okText={t('tpl_router.tpl_dashboard.add_truck_modal.button_add')}
      cancelText={t('tpl_router.tpl_dashboard.add_truck_modal.button_cancel')}
      onOk={handleSubmit(
        onSubmit as unknown as SubmitHandler<{
          licensePlate: string
          description: string
          straightTruck: boolean
          equipmentType: {
            id: unknown
          }
        }>,
      )}
      destroyOnClose
    >
      <StyledForm>
        <StyledRow className="carrier-form-row">
          <StyledForm.Item
            rules={[
              {
                required: true,
                message: t('globals.obligatory_field'),
              },
            ]}
            label={t('tpl_router.tpl_dashboard.add_truck_modal.licensePlate')}
            name="licensePlate"
            hasFeedback
            validateStatus={formState.errors.licensePlate ? 'error' : ''}
            help={formState.errors.licensePlate?.message}
          >
            <Input
              {...register('licensePlate')}
              onChange={e => {
                setValue('licensePlate', e.target.value)
              }}
              maxLength={10}
            />
          </StyledForm.Item>
        </StyledRow>
        <StyledRow className="carrier-form-row">
          <StyledForm.Item
            rules={[
              {
                required: true,
                message: t('globals.obligatory_field'),
              },
            ]}
            label={t('tpl_router.tpl_dashboard.add_truck_modal.description')}
            name="description"
            hasFeedback
            validateStatus={formState.errors.description ? 'error' : ''}
            help={formState.errors.description?.message}
          >
            <Input
              {...register('description')}
              onChange={e => {
                setValue('description', e.target.value)
              }}
              maxLength={20}
            />
          </StyledForm.Item>
        </StyledRow>
        <StyledRow className="carrier-form-row">
          <Form.Item label={t('tpl_router.tpl_dashboard.add_truck_modal.tractor_unit')}>
            <StyledCheckbox
              style={{ margin: '0 0 0 0', justifyContent: 'left' }}
              onChange={e => handleIncludeTrailerChange(e.target.checked)}
              checked={includeTrailer}
            />
          </Form.Item>
        </StyledRow>
        <StyledRow className="carrier-form-row">
          <Form.Item
            label={t('tpl_router.tpl_dashboard.add_truck_modal.equipmentType')}
            name="equipmentType"
            hasFeedback
            validateStatus={formState.errors.equipmentType ? 'error' : ''}
            help={formState.errors.equipmentType?.message}
          >
            <Select
              showSearch
              allowClear
              getPopupContainer={(triggerNode: HTMLElement) =>
                triggerNode.parentNode as HTMLElement
              }
              placeholder={includeTrailer ? null : t('globals.placeholder_select')}
              options={equipments
                ?.filter(equipment => equipment.straightTruck)
                .map(equipment => ({
                  label: equipment.nameShort,
                  value: equipment.id,
                }))}
              onChange={value => {
                setValue('equipmentType', { id: value })
                setValue('straightTruck', true)
              }}
              disabled={includeTrailer}
            />
          </Form.Item>
        </StyledRow>
      </StyledForm>
    </Modal>
  )
}

export { AddTruckModal }
